import React, { ComponentProps } from 'react';

import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from '@/components/ui/command';
import {
  PopoverContent,
  Popover,
  PopoverAnchor,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { getIn, useField, useFormikContext } from 'formik';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Command as CommandPrimitive } from 'cmdk';
import { cn } from '@/utils/utils';
import { Badge } from '@/components/ui/badge';
import { XIcon } from 'lucide-react';
import { InputGroup, InputGroupAddon } from '../ui/input-group';
import { useSetFieldValueWithoutDirty } from '@/hooks/useSetFieldValueWithoutDirty';

export type MultiselectComboboxProps<T> = Omit<
  ComponentProps<typeof CommandInput>,
  'value' | 'onValueChange'
> & {
  placeholder?: string;
  emptyLabel?: React.ReactNode;
  loading?: boolean;
  searchValue: string;
  onSearchValueChange: (query: string) => void;
  items: T[];
  getComboboxItem?: (item: T) => ComboboxItem<T>;
  disabled?: boolean;
  readOnly?: boolean;
  value: T[] | undefined;
  onValueChange: (value?: T[]) => void;
  showAddNew?:
    | (boolean | ((items: T[], searchValue?: string) => boolean))
    | undefined;
  onAddNew?: (searchValue: string) => void;
  addNewLabel?: React.ReactNode | ((searchValue: string) => React.ReactNode);
  resultsFooter?: React.ReactNode;
};
export type ComboboxItem<T> = {
  id: string;
  label: string;
  value: T;
  keywords?: string[];
};

export const MultiselectCombobox = <T,>({
  items,
  loading,
  value,
  onValueChange,
  searchValue,
  onSearchValueChange,
  disabled,
  readOnly,
  placeholder,
  emptyLabel = 'No hay resultados',
  getComboboxItem = (item: T) => ({
    id: String(item),
    label: String(item),
    value: item,
  }),
  showAddNew,
  onAddNew,
  addNewLabel,
  resultsFooter,
  className,
  ...props
}: MultiselectComboboxProps<T>) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [wait, setWait] = React.useState(true);
  const [open, setOpen] = React.useState(false);
  const stopEvent = React.useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);
  const comboboxItems = React.useMemo(
    () => items.map((item) => getComboboxItem(item)),
    [items, getComboboxItem]
  );
  const valueItems = React.useMemo(
    () => value?.map((item) => getComboboxItem(item)) ?? [],
    [value, getComboboxItem]
  );

  // si no espero un toque trata de calcular el ancho del popover mientras lo esta montando y el browser se marea y tira error
  React.useEffect(() => {
    const waitTimer = setTimeout(() => {
      setWait(false);
    }, 300);
    // Clear on unmount: mismo motivo que en Combobox — si no, el setState
    // pendiente se dispara después del teardown de jsdom en CI.
    return () => clearTimeout(waitTimer);
  }, []);

  const onSelectItem = (inputValue: string) => {
    const selectedItem = comboboxItems.find(
      (item) => String(item.id) === inputValue
    );
    if (
      !selectedItem ||
      valueItems.some((item) => item.id === selectedItem.id)
    ) {
      return;
    }
    onSearchValueChange('');
    onValueChange(
      value ? [...value, selectedItem?.value] : [selectedItem?.value]
    );
    setOpen(false);
  };

  const showAddNewItem = React.useMemo(() => {
    if (typeof showAddNew === 'function') {
      return showAddNew(items, searchValue);
    }
    return showAddNew;
  }, [items, searchValue, showAddNew]);

  const onSelectNew = React.useCallback(() => {
    if (showAddNewItem) {
      onAddNew?.(searchValue);
      // Defer clearing search value to allow onAddNew's state update to commit first
      // This prevents resetForm (used by onSearchValueChange) from overwriting the new value
      setTimeout(() => {
        onSearchValueChange('');
      }, 0);
      setOpen(false);
    }
  }, [onAddNew, onSearchValueChange, searchValue, showAddNewItem]);

  const addNewLabelText = React.useMemo(() => {
    if (typeof addNewLabel === 'function') {
      return addNewLabel(searchValue);
    }
    return addNewLabel || 'Crear nuevo';
  }, [addNewLabel, searchValue]);

  return (
    <Popover open={open && !wait} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <Command>
          <InputGroup
            cmdk-input-wrapper=""
            className={cn(
              value?.length && 'flex-wrap gap-1 h-auto max-w-full',
              className
            )}
            data-disabled={disabled}
            data-inline-action=""
            onClick={stopEvent}
            onMouseDown={stopEvent}
            onPointerDown={stopEvent}
            onKeyDown={stopEvent}
          >
            <InputGroupAddon className="flex-wrap gap-1 h-auto max-w-full">
              {!value?.length ? (
                <MagnifyingGlassIcon className="mr-2 size-4 shrink-0 opacity-50" />
              ) : (
                valueItems?.map((valueItem) => (
                  <Badge
                    key={valueItem.id}
                    variant="default"
                    className="inline-flex"
                  >
                    {valueItem.label}
                    {!readOnly && (
                      <button
                        className="ml-1 rounded-full outline-hidden ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            onValueChange(
                              value?.filter(
                                (s) => getComboboxItem(s).id !== valueItem.id
                              )
                            );
                          }
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={() =>
                          onValueChange(
                            value?.filter(
                              (s) => getComboboxItem(s).id !== valueItem.id
                            )
                          )
                        }
                        disabled={disabled || loading}
                      >
                        <XIcon className="size-3 text-muted-foreground hover:text-foreground" />
                      </button>
                    )}
                  </Badge>
                ))
              )}
            </InputGroupAddon>
            {((!disabled && !readOnly) || !value?.length) && (
              <CommandPrimitive.Input
                ref={inputRef}
                data-slot="input-group-control"
                value={searchValue}
                onValueChange={onSearchValueChange}
                placeholder={placeholder}
                onKeyDown={(e) => setOpen(e.key !== 'Escape')}
                onMouseDown={() => setOpen((open) => !!searchValue || !open)}
                onFocus={() => setOpen(true)}
                // onBlur={onInputBlur}
                disabled={disabled}
                className="outline-hidden! ring-0! border-none! shadow-none! disabled:cursor-not-allowed disabled:opacity-50 grow bg-transparent"
                {...props}
              />
            )}
          </InputGroup>
          <PopoverContent
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (
                e.target instanceof Element &&
                e.target === inputRef.current
              ) {
                e.preventDefault();
              }
            }}
            data-inline-action=""
            onClick={stopEvent}
            onMouseDown={stopEvent}
            onPointerDown={stopEvent}
            onKeyDown={stopEvent}
            className="p-0 w-(--radix-popover-trigger-width) max-h-[calc(var(--radix-popover-content-available-height)-4rem)] overflow-y-auto"
            asChild
          >
            <CommandList>
              {loading ? (
                <CommandLoading className="p-4">
                  <Skeleton className="h-6 w-full" />
                </CommandLoading>
              ) : (
                <CommandEmpty>{emptyLabel}</CommandEmpty>
              )}
              {showAddNewItem && (
                <CommandItem
                  value={searchValue}
                  onMouseDown={(e) => e.preventDefault()}
                  onSelect={onSelectNew}
                >
                  {addNewLabelText}
                </CommandItem>
              )}
              {comboboxItems.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.id}
                  keywords={[...(item.keywords || []), item.label]}
                  onMouseDown={(e) => e.preventDefault()}
                  onSelect={onSelectItem}
                  disabled={valueItems.some((i) => i.id === item.id)}
                >
                  {item.label}
                </CommandItem>
              ))}
              {resultsFooter}
            </CommandList>
          </PopoverContent>
        </Command>
      </PopoverAnchor>
    </Popover>
  );
};

export type ConnectedMultiselectComboboxProps<T> = { name: string } & Omit<
  MultiselectComboboxProps<T>,
  'value' | 'onValueChange' | 'searchValue' | 'onSearchValueChange'
>;

export const ConnectedMultiselectCombobox = <T,>({
  name,
  ...props
}: ConnectedMultiselectComboboxProps<T>) => {
  const [{ value, onChange, ...field }] = useField(name);
  const [{ value: searchValue }] = useField(name + '##searchValue');
  const setSearchValueWithoutDirty = useSetFieldValueWithoutDirty<string>(
    name + '##searchValue'
  );
  const setSearchValue = React.useCallback(
    (value: string) => {
      setSearchValueWithoutDirty(value);
    },
    [setSearchValueWithoutDirty]
  );
  const onValueChange = React.useCallback(
    (value: T[] | undefined) => {
      onChange({
        target: {
          name,
          value: value ?? [],
        },
      });
    },
    [onChange, name]
  );
  return (
    <MultiselectCombobox
      value={value}
      onValueChange={onValueChange}
      searchValue={searchValue}
      onSearchValueChange={setSearchValue}
      {...field}
      {...props}
    />
  );
};

export const useComboboxSearchValue = (name: string) => {
  const formik = useFormikContext();
  const searchValue = getIn(formik.values, name + '##searchValue');
  return searchValue ? String(searchValue) : '';
};
